import { ApplicationRef, ComponentRef, EmbeddedViewRef, EnvironmentInjector, Injectable, createComponent, inject } from "@angular/core";

import { Alert, AlertBadge } from "@models";

import { AbstractAlertComponent } from "../components/abstract/abstract-alert.component";
import { AlertComponent } from "../components/alert/alert.component";

@Injectable({ providedIn: "root" })
export class AlertService {
  private readonly inj = inject(EnvironmentInjector);
  private readonly aR = inject(ApplicationRef);

  private readonly MAX = 4;
  private readonly REFS: ComponentRef<AbstractAlertComponent>[] = [];

  private hidden = false;
  private pending: Alert[] = [];

  constructor() {
    document.addEventListener("visibilitychange", this.visibleFn);
  }

  private readonly visibleFn = (): void => {
    this.hidden = document.hidden;

    if (this.hidden) {
      return;
    }

    if (this.pending.length) {
      this.pending.forEach(alert => this.show(alert));
      this.pending = [];
    }

    this.reflow();
  };

  public remove(ref?: ComponentRef<AbstractAlertComponent>): void {
    if (!ref) {
      return;
    }

    if (!ref.hostView.destroyed) {
      this.aR.detachView(ref.hostView);
      ref.destroy();
    }

    const list = this.REFS;
    const index = list.findIndex(it => it === ref);

    if (index !== -1) {
      list.splice(index, 1);
    }

    this.process();
  }

  public clear(): void {
    this.REFS.forEach(ref => this.remove(ref));
  }

  public show(alert: Alert): void {
    if (this.hidden) {
      this.pending.push(alert);
      return;
    }

    const { text, message = "", badge = AlertBadge.I, infinite = false } = alert;

    const ref = createComponent(AlertComponent, { environmentInjector: this.inj });

    const { instance } = ref;

    instance.text.set(text);
    instance.message.set(message);
    instance.badge.set(badge);
    instance.infinite = infinite;
    instance.ref = ref;

    this.aR.attachView(ref.hostView);

    document.body.appendChild((ref.hostView as EmbeddedViewRef<AbstractAlertComponent>).rootNodes[0] as HTMLElement);

    const list = this.REFS;

    list.unshift(ref);

    if (list.length > this.MAX) {
      this.remove(list.at(-1));
    }

    this.process();
  }

  private process(): void {
    this.REFS.forEach((it, index) => {
      it.instance.index = index;
    });
  }

  private reflow(): void {
    const list = this.REFS;

    while (list.length > this.MAX) {
      this.remove(list.at(-1));
    }

    this.process();
  }
}
